// youtubeGeneration.js - FIXED: getVideoTranscript properly called

import { fetchYouTubeTranscriptBackend } from './backendAPI.js';

const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

export const extractYouTubeId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      const videoId = match[1];
      if (videoId.length === 11) {
        return videoId;
      }
    }
  }
  
  if (typeof url === 'string' && url.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return url;
  }
  
  return null;
};

/**
 * 🎬 Fetch transcript - calls backend directly
 */
export const getVideoTranscript = async (videoId) => {
  console.log('📝 Fetching transcript for video:', videoId);
  
  if (!videoId || videoId.length !== 11) {
    console.error('❌ Invalid video ID:', videoId);
    return null;
  }
  
  const transcript = await fetchYouTubeTranscriptBackend(videoId);
  
  if (transcript && typeof transcript === 'string' && transcript.length > 0) {
    console.log(`✅ Transcript received: ${transcript.length} characters`);
    return transcript;
  }
  
  console.warn('⚠️ Transcript not available');
  return null;
};

export const containsYouTubeUrl = (text) => {
  return /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#\s]+)/.test(text);
};

export const fetchVideoDetailsAPI = async (videoId) => {
  if (!YOUTUBE_API_KEY || YOUTUBE_API_KEY === 'YOUR_API_KEY_HERE') {
    console.log('⚠️ YouTube API key not configured');
    return null;
  }

  try {
    console.log('🔑 Using YouTube Data API v3...');
    
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails,statistics&id=${videoId}&key=${YOUTUBE_API_KEY}`
    );

    if (!response.ok) {
      const error = await response.json();
      console.error('❌ YouTube API error:', error);
      return null;
    }

    const data = await response.json();

    if (!data.items || data.items.length === 0) {
      console.error('❌ Video not found via API');
      return null;
    }

    const video = data.items[0];
    const snippet = video.snippet;
    const contentDetails = video.contentDetails;
    const statistics = video.statistics;

    const parseDuration = (duration) => {
      const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      if (!match) return 0;
      const hours = parseInt(match[1] || 0);
      const minutes = parseInt(match[2] || 0);
      const seconds = parseInt(match[3] || 0);
      return hours * 3600 + minutes * 60 + seconds;
    };

    const durationSeconds = parseDuration(contentDetails.duration);

    console.log('✅ 📊 Video details fetched via API:', snippet.title);

    return {
      videoId,
      title: snippet.title,
      description: snippet.description,
      channel: snippet.channelTitle,
      channelId: snippet.channelId,
      publishDate: snippet.publishedAt,
      thumbnail: snippet.thumbnails.maxres?.url || snippet.thumbnails.high?.url,
      tags: snippet.tags || [],
      category: snippet.categoryId,
      duration: durationSeconds,
      viewCount: parseInt(statistics.viewCount || 0),
      likes: parseInt(statistics.likeCount || 0),
      commentCount: parseInt(statistics.commentCount || 0),
      source: 'youtube-api'
    };

  } catch (error) {
    console.error('❌ YouTube API error:', error);
    return null;
  }
};

export const getVideoMetadata = async (videoId) => {
  try {
    console.log('🔍 Fetching basic metadata via oEmbed...');
    const response = await fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch video metadata');
    }
    
    const data = await response.json();
    console.log('✅ 📌 Basic metadata retrieved');
    
    return {
      title: data.title,
      author: data.author_name,
      channelUrl: data.author_url,
      thumbnail: data.thumbnail_url,
      width: data.width,
      height: data.height
    };
  } catch (error) {
    console.error('❌ Error fetching video metadata:', error);
    return null;
  }
};

export const scrapeVideoDetails = async (videoId) => {
  console.log('🕷️ Attempting to scrape video details...');
  const proxies = [
    'https://api.allorigins.win/raw?url=',
    'https://corsproxy.io/?',
    'https://api.codetabs.com/v1/proxy?quest='
  ];
  
  const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
  
  for (let i = 0; i < proxies.length; i++) {
    const proxy = proxies[i];
    console.log(`🔄 Trying proxy ${i + 1}/${proxies.length}...`);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(proxy + encodeURIComponent(videoUrl), {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.log(`⚠️ Proxy ${i + 1} returned status ${response.status}`);
        continue;
      }
      
      const html = await response.text();
      console.log('📄 Retrieved page HTML, parsing...');
      
      const details = {
        title: null,
        description: null,
        channel: null,
        duration: null,
        viewCount: null,
        publishDate: null,
        tags: [],
        category: null,
        likes: null
      };
      
      let titleMatch = html.match(/<title>([^<]+)<\/title>/);
      if (titleMatch) {
        details.title = titleMatch[1].replace(' - YouTube', '').trim();
      }
      
      if (!details.title || details.title === 'YouTube') {
        const ogTitleMatch = html.match(/<meta\s+property=["']og:title["']\s+content=["']([^"']+)["']/i);
        if (ogTitleMatch) {
          details.title = ogTitleMatch[1].trim();
        }
      }
      
      const descMatch = html.match(/<meta name="description" content="([^"]+)"/);
      if (descMatch) {
        details.description = descMatch[1];
      }
      
      const ytInitialDataMatch = html.match(/var ytInitialData = ({.+?});/);
      if (ytInitialDataMatch) {
        try {
          const ytData = JSON.parse(ytInitialDataMatch[1]);
          const videoDetails = ytData?.contents?.twoColumnWatchNextResults?.results?.results?.contents?.[1]?.videoSecondaryInfoRenderer?.attributedDescription?.content;
          if (videoDetails) {
            details.description = videoDetails;
          }
        } catch (e) {
          console.log('⚠️ Could not parse ytInitialData');
        }
      }
      
      const jsonLdMatch = html.match(/<script type="application\/ld\+json">({[^<]+})<\/script>/);
      if (jsonLdMatch) {
        try {
          const jsonData = JSON.parse(jsonLdMatch[1]);
          if (jsonData.name && (!details.title || details.title === 'YouTube')) {
            details.title = jsonData.name;
          }
          if (jsonData.description && !details.description) {
            details.description = jsonData.description;
          }
          if (jsonData.uploadDate) details.publishDate = jsonData.uploadDate;
          if (jsonData.genre) details.category = jsonData.genre;
        } catch (e) {
          console.log('⚠️ Could not parse JSON-LD');
        }
      }
      
      const keywordsMatch = html.match(/<meta name="keywords" content="([^"]+)"/);
      if (keywordsMatch) {
        details.tags = keywordsMatch[1].split(',').map(tag => tag.trim());
      }
      
      const channelMatch = html.match(/"author":"([^"]+)"/);
      if (channelMatch) {
        details.channel = channelMatch[1];
      }
      
      const viewMatch = html.match(/"viewCount":"(\d+)"/);
      if (viewMatch) {
        details.viewCount = parseInt(viewMatch[1]);
      }
      
      const durationMatch = html.match(/"lengthSeconds":"(\d+)"/);
      if (durationMatch) {
        details.duration = parseInt(durationMatch[1]);
      }
      
      const likesMatch = html.match(/"likeCount":(\d+)/);
      if (likesMatch) {
        details.likes = parseInt(likesMatch[1]);
      }
      
      console.log('✅ 📊 YouTube video details scraped:', details.title);
      return details;
      
    } catch (error) {
      console.log(`⚠️ Proxy ${i + 1} failed:`, error.message);
      continue;
    }
  }
  
  console.log('❌ All proxies failed for YouTube scraping');
  return null;
};

export const analyzeVideoContent = (videoDetails, transcript = null) => {
  if (!videoDetails) return null;
  
  console.log('🔍 Analyzing video content...');
  const { title, description, tags, category, duration } = videoDetails;
  
  const combinedText = `${title} ${description} ${transcript || ''}`.toLowerCase();
  
  const topicPatterns = {
    technology: ['tech', 'ai', 'software', 'coding', 'programming', 'computer', 'digital', 'internet', 'app', 'web', 'cyber', 'data', 'cloud', 'algorithm'],
    science: ['science', 'research', 'study', 'experiment', 'discovery', 'physics', 'chemistry', 'biology', 'space', 'nasa', 'laboratory'],
    education: ['tutorial', 'learn', 'how to', 'guide', 'lesson', 'course', 'teach', 'explain', 'education', 'training', 'tips'],
    entertainment: ['funny', 'comedy', 'entertainment', 'fun', 'laugh', 'joke', 'prank', 'vlog', 'daily', 'reaction'],
    news: ['news', 'breaking', 'update', 'report', 'latest', 'today', 'current', 'politics', 'election'],
    business: ['business', 'startup', 'entrepreneur', 'finance', 'money', 'investing', 'market', 'economy', 'stock', 'ipo', 'company'],
    health: ['health', 'fitness', 'workout', 'diet', 'nutrition', 'medical', 'wellness', 'exercise', 'mental health'],
    music: ['music', 'song', 'album', 'artist', 'concert', 'lyrics', 'official video', 'mv', 'audio'],
    gaming: ['gaming', 'game', 'gameplay', 'walkthrough', 'lets play', 'streamer', 'esports', 'console', 'pc'],
    lifestyle: ['lifestyle', 'fashion', 'beauty', 'travel', 'food', 'cooking', 'recipe', 'vlog', 'routine']
  };
  
  const detectedTopics = [];
  for (const [topic, keywords] of Object.entries(topicPatterns)) {
    const matches = keywords.filter(keyword => combinedText.includes(keyword));
    if (matches.length > 0) {
      detectedTopics.push({ topic, relevance: matches.length, keywords: matches });
    }
  }
  
  detectedTopics.sort((a, b) => b.relevance - a.relevance);
  
  const keyPhrases = [];
  
  const titleWords = title.split(' ');
  titleWords.forEach(word => {
    if (word.length > 3 && word[0] === word[0].toUpperCase()) {
      keyPhrases.push(word);
    }
  });
  
  if (transcript) {
    const transcriptWords = transcript.split(/\s+/);
    const wordFrequency = {};
    
    transcriptWords.forEach(word => {
      const cleanWord = word.toLowerCase().replace(/[^a-z]/g, '');
      if (cleanWord.length > 4) {
        wordFrequency[cleanWord] = (wordFrequency[cleanWord] || 0) + 1;
      }
    });
    
    const topWords = Object.entries(wordFrequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([word]) => word);
    
    keyPhrases.push(...topWords);
  }
  
  const formatDuration = (seconds) => {
    if (!seconds) return 'Unknown duration';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  let summary = description?.substring(0, 300) || 'No description available';
  
  if (transcript) {
    if (description && description.includes('Enjoy the videos and music')) {
      summary = transcript.substring(0, 400) + '...';
    }
  }
  
  console.log('✅ 🎯 Content analysis complete');
  
  return {
    primaryTopic: detectedTopics[0]?.topic || 'general',
    allTopics: detectedTopics.map(t => t.topic),
    topicKeywords: detectedTopics[0]?.keywords || [],
    keyPhrases: [...new Set(keyPhrases)].slice(0, 10),
    summary: summary,
    duration: formatDuration(duration),
    durationSeconds: duration,
    tags: tags?.slice(0, 15) || [],
    category: category || 'Unknown',
    hasTranscript: !!transcript,
    transcriptLength: transcript ? transcript.length : 0
  };
};

/**
 * 🎥 Main function - Complete YouTube video analysis
 */
export const getYouTubeVideoAnalysis = async (videoId) => {
  console.log('🎥 ═══════════════════════════════════════');
  console.log('🎥 Analyzing YouTube video:', videoId);
  console.log('🎥 ═══════════════════════════════════════');
  
  try {
    let videoDetails = null;
    let transcript = null;
    
    // Step 1: Fetch video details via API
    if (YOUTUBE_API_KEY && YOUTUBE_API_KEY !== 'YOUR_API_KEY_HERE') {
      console.log('🔑 YouTube API key detected, using API...');
      videoDetails = await fetchVideoDetailsAPI(videoId);
      
      if (videoDetails) {
        console.log('✅ 📊 Video details fetched via API');
      }
    }
    
    // Step 2: Fallback to metadata if API failed
    if (!videoDetails) {
      console.log('🔄 API unavailable, using metadata fallback...');
      
      const metadata = await getVideoMetadata(videoId);
      
      if (metadata) {
        console.log('✅ 📌 Basic metadata fetched');
        
        const scrapedDetails = await scrapeVideoDetails(videoId);
        
        if (scrapedDetails && scrapedDetails.description && 
            scrapedDetails.description !== 'Share your videos with friends, family, and the world') {
          videoDetails = scrapedDetails;
        } else {
          videoDetails = {
            title: metadata.title,
            channel: metadata.author,
            description: metadata.title,
            thumbnail: metadata.thumbnail,
            tags: [],
            duration: null,
            viewCount: null,
            likes: null
          };
        }
      }
    }
    
    if (!videoDetails) {
      console.error('❌ 💔 Could not fetch video details');
      return null;
    }
    
    // Step 3: Fetch transcript
    console.log('📝 ═══════════════════════════════════════');
    console.log('📝 Fetching transcript...');
    console.log('📝 ═══════════════════════════════════════');
    
    transcript = await getVideoTranscript(videoId);
    
    if (transcript) {
      console.log(`✅ 🎉 Transcript fetched: ${transcript.length} characters`);
    } else {
      console.warn('⚠️ ❌ No transcript available');
    }
    
    // Step 4: Analyze content
    console.log('🔍 ═══════════════════════════════════════');
    const analysis = analyzeVideoContent(videoDetails, transcript);
    
    // Step 5: Build final result
    const result = {
      videoId,
      title: videoDetails.title,
      channel: videoDetails.channel,
      channelUrl: videoDetails.channelUrl || `https://www.youtube.com/channel/${videoDetails.channelId}`,
      thumbnail: videoDetails.thumbnail,
      description: videoDetails.description,
      duration: analysis?.duration || 'Unknown',
      durationSeconds: videoDetails.duration || null,
      viewCount: videoDetails.viewCount || null,
      publishDate: videoDetails.publishDate || null,
      likes: videoDetails.likes || null,
      topics: analysis?.allTopics || [],
      primaryTopic: analysis?.primaryTopic || 'general',
      topicKeywords: analysis?.topicKeywords || [],
      keyPhrases: analysis?.keyPhrases || [],
      tags: videoDetails.tags || [],
      category: videoDetails.category || null,
      summary: analysis?.summary || videoDetails.description || videoDetails.title,
      transcript: transcript || null,
      transcriptLength: transcript ? transcript.length : 0,
      hasTranscript: !!transcript,
      source: videoDetails.source || (transcript ? 'full-with-transcript' : 'metadata-only')
    };
    
    console.log('═══════════════════════════════════════');
    console.log('✅ 🎉 YouTube video analysis complete!');
    console.log('═══════════════════════════════════════');
    console.log('📊 Summary:');
    console.log('  📺 Title:', result.title);
    console.log('  📝 Has Transcript:', result.hasTranscript ? '✅ YES' : '❌ NO');
    console.log('  📏 Transcript Length:', result.transcriptLength, 'chars');
    console.log('  🎯 Topics:', result.topics.join(', '));
    console.log('═══════════════════════════════════════');
    
    return result;
    
  } catch (error) {
    console.error('❌ 💥 Error analyzing YouTube video:', error);
    return null;
  }
};

export const generatePodcastContext = (videoAnalysis) => {
  if (!videoAnalysis) return '';
  
  const { 
    title, 
    channel, 
    description, 
    topics, 
    duration, 
    keyPhrases,
    transcript,
    transcriptLength,
    topicKeywords,
    viewCount,
    likes
  } = videoAnalysis;
  
  let context = `YouTube Video Analysis:\n\n`;
  context += `📺 TITLE: "${title}"\n`;
  context += `👤 CHANNEL: ${channel}\n`;
  context += `⏱️ DURATION: ${duration}\n`;
  
  if (viewCount) {
    context += `👁️ VIEWS: ${viewCount.toLocaleString()}\n`;
  }
  
  if (likes) {
    context += `👍 LIKES: ${likes.toLocaleString()}\n`;
  }
  
  context += `\n📊 ANALYSIS:\n`;
  context += `- Primary Topic: ${topics[0] || 'general'}\n`;
  context += `- Related Topics: ${topics.slice(1).join(', ') || 'none'}\n`;
  context += `- Key Themes: ${topicKeywords.slice(0, 8).join(', ')}\n`;
  context += `- Important Terms: ${keyPhrases.slice(0, 8).join(', ')}\n`;
  
  context += `\n📝 DESCRIPTION:\n${description}\n`;
  
  if (transcript && transcriptLength > 0) {
    context += `\n\n🎯 VIDEO TRANSCRIPT (${transcriptLength} characters):\n`;
    context += `${transcript.substring(0, 4000)}${transcriptLength > 4000 ? '...' : ''}\n`;
    context += `\n⚠️ IMPORTANT: The transcript above contains the ACTUAL SPOKEN CONTENT from the video.\n`;
  } else {
    context += `\n⚠️ Note: Transcript not available for this video.\n`;
  }
  
  return context.trim();
};

export const extractKeyQuotes = (transcript, duration) => {
  if (!transcript || transcript.length < 100 || !duration) return [];
  
  const quotes = [];
  const sentences = transcript.split(/[.!?]+/).filter(s => s.trim().length > 20);
  
  const charPerSecond = transcript.length / duration;
  
  sentences.forEach((sentence) => {
    const trimmed = sentence.trim();
    const charPosition = transcript.indexOf(trimmed);
    const timeInSeconds = Math.floor(charPosition / charPerSecond);
    const timeInMinutes = Math.floor(timeInSeconds / 60);
    const remainingSeconds = timeInSeconds % 60;
    const timestamp = `${timeInMinutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    
    if (trimmed.includes('?') && trimmed.split(' ').length > 5) {
      quotes.push({
        type: 'question',
        text: trimmed.substring(0, 150),
        timestamp,
        timeInSeconds
      });
    }
    
    if (trimmed.includes('!') || 
        /\b(must|should|need to|have to|critical|important|essential|wrong|right)\b/i.test(trimmed)) {
      quotes.push({
        type: 'statement',
        text: trimmed.substring(0, 150),
        timestamp,
        timeInSeconds
      });
    }
    
    if (/\b(but|however|although|disagree|wrong|actually|instead|versus|vs|compared)\b/i.test(trimmed)) {
      quotes.push({
        type: 'debate',
        text: trimmed.substring(0, 150),
        timestamp,
        timeInSeconds
      });
    }
  });
  
  return quotes
    .sort((a, b) => a.timeInSeconds - b.timeInSeconds)
    .slice(0, 10);
};

export const extractAndRemoveVideoUrl = (text) => {
  const urlPatterns = [
    /https?:\/\/(?:www\.)?youtube\.com\/watch\?[^\s]+/gi,
    /https?:\/\/youtu\.be\/[^\s]+/gi,
    /https?:\/\/(?:www\.)?youtube\.com\/embed\/[^\s]+/gi,
    /https?:\/\/(?:www\.)?youtube\.com\/shorts\/[^\s]+/gi
  ];
  
  const videoId = extractYouTubeId(text);
  
  if (!videoId) {
    return { cleanText: text, videoId: null };
  }
  
  let cleanText = text;
  urlPatterns.forEach(pattern => {
    cleanText = cleanText.replace(pattern, '');
  });
  
  cleanText = cleanText.replace(/\s+/g, ' ').trim();
  
  return { cleanText, videoId };
};

export default {
  extractYouTubeId,
  containsYouTubeUrl,
  fetchVideoDetailsAPI,
  getVideoMetadata,
  getVideoTranscript,
  scrapeVideoDetails,
  analyzeVideoContent,
  getYouTubeVideoAnalysis,
  generatePodcastContext,
  extractAndRemoveVideoUrl,
  extractKeyQuotes
};